/**
 * API error handling with human-readable messages in Russian.
 *
 * Matches the backend error format from app/web/routers/api/errors.py:
 * { "error": "error_code", "message": "...", "request_id": "..." }
 */

import type { ApiErrorResponse } from '@/types/api';

const TRACEPARENT_REGEX = /^[\da-f]{2}-([\da-f]{32})-([\da-f]{16})-[\da-f]{2}$/i;
const RETRY_AFTER_MAX_DELAY_MS = 5_000;

const WEB_ADMIN_SESSION_ERROR_CODES = new Set(['web_admin_auth_required', 'auth_required']);

/**
 * Human-readable error messages in Russian for common error codes.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // HTTP status codes
  400: 'Некорректный запрос',
  401: 'Требуется авторизация',
  403: 'Доступ запрещен',
  404: 'Ресурс не найден',
  409: 'Конфликт данных',
  422: 'Некорректные данные запроса',
  429: 'Слишком много запросов',
  500: 'Внутренняя ошибка сервера',
  502: 'Сервер недоступен',
  503: 'Сервис временно недоступен',
  504: 'Превышено время ожидания',

  // Backend error codes
  web_admin_auth_required: 'Требуется авторизация в панели администратора',
  AUTH_REQUIRED: 'Требуется авторизация в панели администратора',
  admin_required: 'Требуется роль администратора',
  validation_error: 'Некорректные данные запроса',
  invalid_quantity: 'Некорректное количество',
  not_found: 'Ресурс не найден',
  internal_error: 'Внутренняя ошибка сервера',
  http_error: 'Ошибка при выполнении запроса',
  tenant_runtime_busy: 'Система сейчас занята. Повторите попытку через пару секунд.',
  stale_auth_session_response: 'Ответ от предыдущей сессии был отменён.',
  stale_document:
    'Документ уже изменён другим пользователем. Обновите данные и повторите действие.',
  invalid_expected_updated_at:
    'Некорректная версия документа. Обновите страницу и повторите действие.',
  pos_not_configured: 'POS модуль не инициализирован. Примените миграции базы данных.',

  // Network errors
  network_error: 'Ошибка сети. Проверьте подключение к интернету',
  timeout: 'Превышено время ожидания ответа от сервера',
};

/**
 * Get a human-readable error message for an error code or status.
 */
function getErrorMessage(errorCode: string | number): string {
  return ERROR_MESSAGES[errorCode] ?? 'Произошла непредвиденная ошибка';
}

function localizeKnownBackendMessage(errorCode: string, message: string): string {
  const normalized = message.trim().toLowerCase();
  if (
    errorCode === 'invalid_quantity' &&
    normalized.includes('pcs/portion requires integer quantity')
  ) {
    return 'Для единиц "шт/порция" укажите целое количество.';
  }
  if (
    errorCode === 'invalid_quantity' &&
    /^item\s+\d+:\s+quantity must be positive$/i.test(message.trim())
  ) {
    return 'Количество в позиции должно быть больше нуля.';
  }
  if (errorCode === 'sale_has_confirmed_shipments') {
    return 'Нельзя удалить документ с подтверждёнными отгрузками. Сначала отмените отгрузки.';
  }
  if (errorCode === 'sale_has_payments') {
    return 'Нельзя удалить документ с оплатами. Сначала отмените оплаты.';
  }
  if (errorCode === 'tenant_runtime_busy') {
    return 'Система сейчас занята. Повторите попытку через пару секунд.';
  }
  if (errorCode === 'product_uom_change_blocked_by_active_recipe_version') {
    return 'Единицу товара нельзя изменить автоматически, пока товар используется как ингредиент в активной рецептуре.';
  }
  if (errorCode === 'ksef_seller_required_fields_missing') {
    const fieldList = message.replace(/^.*fields:\s*/i, '').trim();
    return fieldList
      ? `Для отправки в KSeF заполните реквизиты выбранной фирмы: ${fieldList}.`
      : 'Для отправки в KSeF заполните реквизиты выбранной фирмы.';
  }
  return message;
}

/**
 * API error class with structured error information.
 */
export class ApiError extends Error {
  /**
   * HTTP status code (0 for network errors).
   */
  readonly statusCode: number;

  /**
   * Backend error code (e.g., "validation_error", "not_found").
   */
  readonly error: string;

  /**
   * Request ID for debugging/support.
   */
  readonly requestId: string | undefined;

  /**
   * Field-level validation errors.
   */
  readonly fieldErrors: Record<string, string> | undefined;

  /**
   * Structured error metadata from the backend.
   */
  readonly meta: Record<string, unknown> | undefined;

  /**
   * Trace correlation IDs extracted from `traceparent` response header.
   */
  readonly traceId: string | undefined;
  readonly spanId: string | undefined;
  readonly retryAfterMs: number | undefined;

  constructor(
    statusCode: number,
    error: string,
    message: string,
    requestId?: string,
    fieldErrors?: Record<string, string>,
    traceId?: string,
    spanId?: string,
    retryAfterMs?: number,
    meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.error = error;
    this.requestId = requestId;
    this.fieldErrors = fieldErrors;
    this.traceId = traceId;
    this.spanId = spanId;
    this.retryAfterMs = retryAfterMs;
    this.meta = meta;

    // Maintain proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Create an ApiError from a fetch Response and parsed body.
   *
   * Extracts request_id from the body, falling back to X-Request-ID header.
   */
  static fromResponse(response: Response, body: unknown): ApiError {
    const statusCode = response.status;

    // Try to extract error info from body
    let error = 'http_error';
    let message = getErrorMessage(statusCode);
    let requestId: string | undefined;
    let fieldErrors: Record<string, string> | undefined;
    let meta: Record<string, unknown> | undefined;

    const normalizedError = normalizeErrorShape(body);
    if (normalizedError) {
      error = normalizedError.error;
      // Use backend message if provided, otherwise use our localized message
      message = normalizedError.message || getErrorMessage(normalizedError.error) || message;
      message = localizeKnownBackendMessage(error, message);
      requestId = normalizedError.requestId;
      fieldErrors = normalizedError.fieldErrors;
      meta = normalizedError.meta;
    }

    // Fall back to X-Request-ID header if not in body
    if (!requestId) {
      requestId = response.headers.get('X-Request-ID') ?? undefined;
    }

    const traceparent = response.headers.get('traceparent');
    const traceContext = parseTraceparent(traceparent);
    const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'));

    return new ApiError(
      statusCode,
      error,
      message,
      requestId,
      fieldErrors,
      traceContext.traceId,
      traceContext.spanId,
      retryAfterMs ?? undefined,
      meta,
    );
  }

  /**
   * Create an ApiError for network failures (no response received).
   */
  static networkError(_originalError?: Error): ApiError {
    const message = getErrorMessage('network_error');
    return new ApiError(0, 'network_error', message);
  }

  /**
   * Returns true if this is a client error (4xx).
   */
  isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * Returns true if this is a server error (5xx).
   */
  isServerError(): boolean {
    return this.statusCode >= 500;
  }

  /**
   * Returns true if this is a network error (no response).
   */
  isNetworkError(): boolean {
    return this.statusCode === 0;
  }

  /**
   * Returns true if this is an authentication error.
   */
  isAuthError(): boolean {
    return (
      this.statusCode === 401 ||
      this.error === 'web_admin_auth_required' ||
      this.error === 'admin_required'
    );
  }

  /**
   * Returns true if this indicates an expired/missing web-admin session.
   * Use this for global auto-logout behavior.
   */
  isSessionAuthError(): boolean {
    return WEB_ADMIN_SESSION_ERROR_CODES.has(this.error.toLowerCase());
  }

  /**
   * Returns true when a response completed after its auth session was replaced.
   */
  isStaleAuthSessionResponse(): boolean {
    return this.error === 'stale_auth_session_response';
  }

  /**
   * Returns true if this is a validation error.
   */
  isValidationError(): boolean {
    return this.statusCode === 422 || this.error === 'validation_error';
  }

  /**
   * Returns true when the tenant runtime admission gate rejected the request.
   */
  isTenantRuntimeBusy(): boolean {
    return this.statusCode === 429 && this.error === 'tenant_runtime_busy';
  }
}

function parseRetryAfterMs(value: string | null): number | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, RETRY_AFTER_MAX_DELAY_MS);
  }
  const atMs = Date.parse(trimmed);
  if (Number.isNaN(atMs)) {
    return null;
  }
  return Math.max(0, Math.min(atMs - Date.now(), RETRY_AFTER_MAX_DELAY_MS));
}

/**
 * Type guard to check if a value is an ApiErrorResponse.
 */
function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiErrorResponse).error === 'string' &&
    'message' in value &&
    typeof (value as ApiErrorResponse).message === 'string'
  );
}

type NormalizedErrorShape = {
  error: string;
  message?: string;
  requestId?: string;
  fieldErrors?: Record<string, string>;
  meta?: Record<string, unknown>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseErpErrorShape(value: unknown): NormalizedErrorShape | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const errorValue = value.error;
  if (!isRecord(errorValue)) {
    return undefined;
  }
  const code = errorValue.code;
  if (typeof code !== 'string' || !code.trim()) {
    return undefined;
  }
  const normalized: NormalizedErrorShape = { error: code };
  if (typeof errorValue.message === 'string') {
    normalized.message = errorValue.message;
  }
  if (typeof value.request_id === 'string') {
    normalized.requestId = value.request_id;
  }
  return normalized;
}

function normalizeErrorShape(value: unknown, depth = 0): NormalizedErrorShape | undefined {
  if (depth > 2) {
    return undefined;
  }

  if (isApiErrorResponse(value)) {
    const normalized: NormalizedErrorShape = {
      error: value.error,
      message: value.message,
    };
    if (typeof value.request_id === 'string') {
      normalized.requestId = value.request_id;
    }
    if (value.field_errors) {
      normalized.fieldErrors = value.field_errors;
    }
    if (isRecord(value.meta)) {
      normalized.meta = value.meta;
    }
    return normalized;
  }

  const erpShape = parseErpErrorShape(value);
  if (erpShape) {
    return erpShape;
  }

  if (isRecord(value) && 'detail' in value) {
    return normalizeErrorShape(value.detail, depth + 1);
  }

  return undefined;
}

export function parseTraceparent(value: string | null | undefined): {
  traceId: string | undefined;
  spanId: string | undefined;
} {
  if (!value) {
    return { traceId: undefined, spanId: undefined };
  }
  const normalized = value.trim();
  const match = normalized.match(TRACEPARENT_REGEX);
  if (!match) {
    return { traceId: undefined, spanId: undefined };
  }
  const traceId = match[1];
  const spanId = match[2];
  if (!traceId || !spanId) {
    return { traceId: undefined, spanId: undefined };
  }
  return {
    traceId: traceId.toLowerCase(),
    spanId: spanId.toLowerCase(),
  };
}
